import { useLazyQuery } from '@apollo/client';
import { FC, Fragment, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import {
	GET_BUSINESS_BRAND_BY_CATEGORY,
	GET_ALL_BUSINESS_PROVIDER,
	FILTER_BUSINESS_PROVIDER
} from '../../graphql/queries/businessProvider';
import './index.scss';
import { DEFAULT_LABELS } from './model';
import { ErrorState, RootState } from '../../redux/AppReducer';
import { AppActions } from '../../redux/AppActions';
import { GENERAL_ERRORS, serverError } from '../../common/constants';
import { handleTracking } from '../../helpers/handelTracking';
import { ITrackingEvent, TrackingEventStatus, TrackingEventType } from '../../common/view-model';
import { v4 as uuidv4 } from 'uuid';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../redux/store';
import { AuthActions } from '../../redux/AuthActions';
import { FilterActions } from '../../redux/FilterActions';

export interface IBusinessServices {
	name: string;
	id: number;
	brands: any;
}

const BusinessProvidersPage: FC = () => {
	const dispatch = useAppDispatch();
	const [providers, setProviders] = useState<any>([]);
	const [searchParams] = useSearchParams();
	const category = Number(searchParams.get('category')) || null;
	const userState = useSelector((state: RootState) => state.appState.userState?.user);
	const tokenHasError = useSelector(
		({ appState: state }: RootState) => state.authState?.tokenHasError
	);
	const businessProviderSelected = useSelector(
		({ appState: state }: RootState) => state.businessProviderState?.providerSelected
	);
	const [getBusinessProviders, { loading, error, data }] = useLazyQuery(
		GET_BUSINESS_BRAND_BY_CATEGORY
	);

	const [getAllBusinessProvidersData, { data: allBizData }] =
		useLazyQuery(GET_ALL_BUSINESS_PROVIDER);

	const [filterBusinessProviders, { data: filterBizData }] = useLazyQuery(FILTER_BUSINESS_PROVIDER);

	const [totalProviders, setTotalProviders] = useState<number>(0);
	const trackingBusinessEvent = (brand: any) => {
		const trackingPayload: ITrackingEvent = {
			transactionId: uuidv4(),
			status: TrackingEventStatus.SUCCESS,
			type: TrackingEventType.BUSINESS_PROVIDER_BRAND,
			timeStamp: new Date().toISOString(),
			entityId: brand.id || '',
			eventPayload: {
				id: brand!.id || '',
				name: `busines_provider-category`,
				type: `business-provider-tracking`,
				url: window.location.href,
				tags: [],
				metadata: {
					user_type: userState?.user?.user_type,
					user_id: userState?.user?.id,
					environment: import.meta.env.VITE_ENV_NAME,
					brand_name: brand.name || ''
				}
			}
		};
		handleTracking(trackingPayload);
	};
	const handleError = (error: ErrorState) => dispatch(AppActions.setGeneralError(error));
	const handleUnauthorized = () => dispatch(AppActions.unauthorizedError());

	useEffect(() => {
		if (tokenHasError === 0) {
			getBusinessProviders({
				variables: {
					id: category
				}
			});
		}
	}, [category]);

	useEffect(() => {
		if (data) {
			console.log('Data 2: ', data);
		}
	}, [data]);

	useEffect(() => {
		getAllBusinessProvidersData();
	}, []);

	useEffect(() => {
		if (allBizData) {
			console.log('Data 3: ', allBizData);
		}
	}, [allBizData]);

	useEffect(() => {
		filterBusinessProviders({
			variables: {
				bizfilters: [
					{
						key: 'E-tailers',
						ids: '162'
					}
				]
			}
		});
	}, []);

	useEffect(() => {
		if (filterBizData) {
			console.log('Data 4: ', filterBizData);
		}
	}, [filterBizData]);

	useEffect(() => {
		if (!loading && data?.brandsByBusinessService) {
			const { __typename, ...rest } = data.brandsByBusinessService;
			const bizArray: IBusinessServices[] = Object.values(rest);
			setProviders(bizArray);
			const allBrands = bizArray.flatMap(biz => biz.brands.map((brand: any) => brand.name));
			const uniqueSet = new Set(allBrands);
			setTotalProviders(uniqueSet.size);
		}
	}, [data, loading]);

	useEffect(() => {
		if (error?.message === 'Unauthorized') {
			if (tokenHasError === 0) {
				dispatch(AuthActions.setTokenError(1));
			}
		}
		if (error?.message === GENERAL_ERRORS.USER_TOKEN_EXPIRED) {
			handleUnauthorized();
		}
		if (error) {
			handleError(serverError);
		}
	}, [error]);

	useEffect(() => {
		if (providers) {
			if (businessProviderSelected && businessProviderSelected !== '') {
				let element: HTMLElement = document.getElementById(businessProviderSelected) as HTMLElement;
				if (element) {
					element.scrollIntoView({ block: 'start' });
				}
				setTimeout(() => {
					dispatch(
						FilterActions.updateBusinessProviderState({
							providerSelected: ''
						})
					);
				}, 1000);
			}
		}
	}, [providers]);

	const handleSetCategory = (provider: any) => {
		dispatch(
			FilterActions.updateBusinessProviderState({
				providerSelected: `business-provider-${provider.id}`
			})
		);
	};

	return (
		<>
			{providers && !error && !loading && (
				<div className='list-providers-container'>
					<h3 className='section-title'>{DEFAULT_LABELS.sectionTitle}</h3>
					<div className='providers-container'>
						<span className='providers-count'>{`${totalProviders} ${DEFAULT_LABELS.totalProviders}`}</span>
						{providers.length > 0 &&
							providers.map((provider: IBusinessServices, indexProvider: any) => (
								<Fragment key={indexProvider}>
									{provider.brands.length > 0 && (
										<div
											className='provider-section'
											key={`provider_${indexProvider}`}
											id={`business-provider-${provider.id}`}
										>
											<div className='category-title'>
												<h3 className='title'>{provider.name}</h3>
											</div>
											<div className='brands-container'>
												<div className='group'>
													{provider.brands.map((brand: any, indexBrand: number) => {
														return (
															<div
																className='brand'
																key={`provider_index_brand_${indexBrand}`}
																data-test={`provider_${indexProvider}_${indexBrand}`}
																onClick={() => trackingBusinessEvent(brand)}
															>
																<Link
																	to={`/businessProvider/${brand.id}`}
																	className='text'
																	onClick={() => handleSetCategory(provider)}
																>
																	{brand.name}
																</Link>
															</div>
														);
													})}
												</div>
											</div>
										</div>
									)}
								</Fragment>
							))}
					</div>
				</div>
			)}
		</>
	);
};

export default BusinessProvidersPage;
